import React, { useState } from 'react';
import { View } from 'react-native';
import { SegmentedButtons } from 'react-native-paper';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramPowerCalculations } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';

const KEY = 'tabai.calc.electricalPower.v1';

type Phase = '1' | '3';
type Fields = {
  voltage: string;
  current: string;
  powerFactor: string;
  phases: Phase;
  realPower: string;
};
const EMPTY: Fields = { voltage: '', current: '', powerFactor: '', phases: '1', realPower: '' };

/**
 * Electrical Power — P = k·V·I·PF  (k = 1 for 1φ, √3 for 3φ)
 * Bidirectional: fill any 3 of {V, I, PF, P} to solve the 4th. Phase is a selector.
 * Apparent power S = k·V·I is always shown as a derived output.
 */
const ElectricalPowerCalculationsScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const { voltage, current, powerFactor, realPower } = fields;
    const filled = [voltage, current, powerFactor, realPower].filter((v) => v !== '').length;
    if (filled < 3) { setError('Fill any 3 of {Voltage, Current, Power Factor, Real Power} to solve the 4th.'); return; }
    if (filled === 4) { setError('All 4 values are filled — clear one to solve for it.'); return; }

    const k = fields.phases === '3' ? Math.sqrt(3) : 1;
    const v = parseFloat(voltage);
    const i = parseFloat(current);
    const pf = parseFloat(powerFactor);
    const p = parseFloat(realPower);

    if (powerFactor !== '' && (pf < 0 || pf > 1)) { setError('Power factor must be between 0 and 1.'); return; }

    if (realPower === '') {
      if (!(v > 0) || !(i > 0) || !(pf >= 0)) { setError('V, I must be > 0, PF ≥ 0.'); return; }
      setSolvedFor('realPower');
      set({ realPower: (k * v * i * pf).toFixed(2) });
    } else if (voltage === '') {
      if (!(i > 0) || !(pf > 0)) { setError('I and PF must be > 0.'); return; }
      setSolvedFor('voltage');
      set({ voltage: (p / (k * i * pf)).toFixed(2) });
    } else if (current === '') {
      if (!(v > 0) || !(pf > 0)) { setError('V and PF must be > 0.'); return; }
      setSolvedFor('current');
      set({ current: (p / (k * v * pf)).toFixed(3) });
    } else if (powerFactor === '') {
      if (!(v > 0) || !(i > 0)) { setError('V and I must be > 0.'); return; }
      const solved = p / (k * v * i);
      if (solved < 0 || solved > 1) { setError(`Computed PF=${solved.toFixed(3)} outside [0,1] — check inputs.`); return; }
      setSolvedFor('powerFactor');
      set({ powerFactor: solved.toFixed(3) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  // Derived apparent power for display
  const k = fields.phases === '3' ? Math.sqrt(3) : 1;
  const v = parseFloat(fields.voltage);
  const i = parseFloat(fields.current);
  const apparentPower = Number.isFinite(v) && Number.isFinite(i) && v > 0 && i > 0 ? (k * v * i).toFixed(2) : null;

  const result = [
    fields.voltage && { label: 'Voltage', value: fields.voltage, unit: 'V', emphasis: solvedFor === 'voltage' },
    fields.current && { label: 'Current', value: fields.current, unit: 'A', emphasis: solvedFor === 'current' },
    fields.powerFactor && { label: 'Power Factor', value: fields.powerFactor, emphasis: solvedFor === 'powerFactor' },
    fields.realPower && { label: 'Real Power', value: fields.realPower, unit: 'W', emphasis: solvedFor === 'realPower' },
    apparentPower && { label: 'Apparent Power', value: apparentPower, unit: 'VA' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Power Calculations"
      subtitle="Solve for V, I, PF, or P — fill any 3 of 4"
      formula={
        <>
          <FormulaLine>1φ: P = V × I × PF,  S = V × I</FormulaLine>
          <FormulaLine>3φ: P = √3 × V × I × PF,  S = √3 × V × I</FormulaLine>
          <FormulaNote>P = Real Power (W), S = Apparent Power (VA), PF = Power Factor (0..1).</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: P = k·V·I·PF"
      diagram={<DiagramPowerCalculations />}
      outputSlots={{
        volts: fields.voltage ? parseFloat(fields.voltage) : undefined,
        amps: fields.current ? parseFloat(fields.current) : undefined,
        power_factor: fields.powerFactor ? parseFloat(fields.powerFactor) : undefined,
        power_w: fields.realPower ? parseFloat(fields.realPower) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.volts != null && !fields.voltage) patch.voltage = values.volts.toFixed(2);
        if (values.amps != null && !fields.current) patch.current = values.amps.toFixed(3);
        if (values.power_factor != null && !fields.powerFactor) patch.powerFactor = values.power_factor.toFixed(3);
        if (values.power_w != null && !fields.realPower) patch.realPower = values.power_w.toFixed(2);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <SegmentedButtons
        value={fields.phases}
        onValueChange={(v) => set({ phases: v as Phase })}
        buttons={[
          { value: '1', label: 'Single Phase' },
          { value: '3', label: 'Three Phase' },
        ]}
        style={{ marginBottom: 16 }}
      />
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Voltage" value={fields.voltage} onChangeText={(val) => set({ voltage: val })} rightText="V" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Current" value={fields.current} onChangeText={(val) => set({ current: val })} rightText="A" maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Power Factor (0..1)" value={fields.powerFactor} onChangeText={(val) => set({ powerFactor: val })} maxDecimals={3} onSubmitEditing={calculate} />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField label="Real Power" value={fields.realPower} onChangeText={(val) => set({ realPower: val })} rightText="W" maxDecimals={2} onSubmitEditing={calculate} />
      </View>
    </CalcShell>
  );
};

export default ElectricalPowerCalculationsScreen;
